// Regole del pannello "Orari e chiusure" senza interfaccia, cosi' si provano
// con i test: stato del form, date senza anno, copia degli orari, payload.
//
// Periodi e chiusure sono GIORNO + MESE (si ripetono ogni anno). Lo stato del
// form tiene tutto come testo ("" = vuoto) per i campi controllati; `toPayload`
// lo riporta a numeri o null come lo vuole il server.

export const MONTHS = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

export const WEEKDAYS = [
  "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica",
];

const DATE_KEYS = ["start_day", "start_month", "end_day", "end_month"];
const TIME_KEYS = ["opens", "closes", "break_from", "break_to"];
// Anno bisestile di riferimento: il 29/2 esiste e ogni calcolo e' coerente col server.
const REFERENCE_YEAR = 2000;

let counter = 0;
const newKey = () => {
  counter += 1;
  return `riga-${counter}`;
};

const text = (value) => (value === null || value === undefined ? "" : String(value));

export function emptyDays() {
  return WEEKDAYS.map((name, weekday) => ({
    weekday, name, is_open: false, opens: "", closes: "", break_from: "", break_to: "",
  }));
}

export function emptyPeriod() {
  return {
    key: newKey(), id: null, remove: false, name: "",
    start_day: "", start_month: "", end_day: "", end_month: "", days: emptyDays(),
  };
}

export function emptyClosure() {
  return {
    key: newKey(), id: null, remove: false, note: "",
    start_day: "", start_month: "", end_day: "", end_month: "",
  };
}

// Dati del server -> stato del form.
export function fromServer(state) {
  const periods = (state.periods || []).map((period) => ({
    key: newKey(), id: period.id, remove: false, name: period.name || "",
    ...Object.fromEntries(DATE_KEYS.map((k) => [k, text(period[k])])),
    days: WEEKDAYS.map((name, weekday) => {
      const day = (period.days || [])[weekday] || {};
      return {
        weekday, name, is_open: Boolean(day.is_open),
        ...Object.fromEntries(TIME_KEYS.map((k) => [k, text(day[k])])),
      };
    }),
  }));
  const closures = (state.closures || []).map((closure) => ({
    key: newKey(), id: closure.id, remove: false, note: closure.note || "",
    ...Object.fromEntries(DATE_KEYS.map((k) => [k, text(closure[k])])),
  }));
  // Un negozio nuovo parte con un periodo vuoto pronto da compilare.
  return { periods: periods.length ? periods : [emptyPeriod()], closures };
}

const num = (value) => (value === "" || value === null || value === undefined ? null : Number(value));

// Stato del form -> corpo della richiesta. Le righe salvate e tolte restano
// nell'elenco con `remove`: e' cosi' che il server le cancella.
export function toPayload(draft) {
  const dates = (item) => Object.fromEntries(DATE_KEYS.map((k) => [k, num(item[k])]));
  return {
    periods: draft.periods.map((period) => ({
      id: period.id, remove: period.remove, name: period.name, ...dates(period),
      days: period.days.map((day) => ({
        is_open: day.is_open,
        ...Object.fromEntries(TIME_KEYS.map((k) => [k, day[k]])),
      })),
    })),
    closures: draft.closures.map((closure) => ({
      id: closure.id, remove: closure.remove, note: closure.note, ...dates(closure),
    })),
  };
}

export function isValidDayMonth(month, day) {
  const m = Number(month);
  const d = Number(day);
  if (!Number.isInteger(m) || !Number.isInteger(d) || m < 1 || m > 12 || d < 1) return false;
  const date = new Date(REFERENCE_YEAR, m - 1, d);
  return date.getMonth() === m - 1 && date.getDate() === d;
}

const two = (n) => String(n).padStart(2, "0");

export function dayMonthLabel(day, month) {
  return `${two(day)}/${two(month)}`;
}

export function hasAllDates(item) {
  return DATE_KEYS.every((k) => item[k] !== "" && item[k] !== null && item[k] !== undefined);
}

export function hasNoDates(item) {
  return DATE_KEYS.every((k) => item[k] === "" || item[k] === null || item[k] === undefined);
}

// Il titolo "a colpo d'occhio" di un periodo.
export function periodBadge(period) {
  if (hasNoDates(period)) return "Tutto l'anno";
  if (!hasAllDates(period)) return "Date da completare";
  return `dal ${dayMonthLabel(period.start_day, period.start_month)} al ${dayMonthLabel(period.end_day, period.end_month)}`;
}

function shiftDay(month, day, delta) {
  const date = new Date(REFERENCE_YEAR, Number(month) - 1, Number(day) + delta);
  return { month: date.getMonth() + 1, day: date.getDate() };
}

// Le date che restano fuori da un periodo: se il primo va dal 01/06 al 01/11 il
// resto dell'anno va dal 02/11 al 31/05. null se il periodo non ha date complete
// o copre gia' tutto l'anno.
export function complementDates(period) {
  if (!hasAllDates(period)) return null;
  if (!isValidDayMonth(period.start_month, period.start_day) || !isValidDayMonth(period.end_month, period.end_day)) {
    return null;
  }
  const next = shiftDay(period.end_month, period.end_day, 1);
  const previous = shiftDay(period.start_month, period.start_day, -1);
  if (next.month === Number(period.start_month) && next.day === Number(period.start_day)) return null;
  return {
    start_day: String(next.day), start_month: String(next.month),
    end_day: String(previous.day), end_month: String(previous.month),
  };
}

// "Copia gli orari sugli altri giorni aperti": prende il PRIMO giorno aperto con
// apertura e chiusura compilate (pausa compresa) e lo scrive su tutti gli altri
// giorni aperti. Restituisce i giorni nuovi, quanti ne ha toccati e il giorno di
// partenza (null se non ce n'e' nessuno da cui copiare).
export function copyFirstOpenDay(days) {
  const source = days.find((day) => day.is_open && day.opens && day.closes);
  if (!source) return { days, copied: 0, source: null };
  let copied = 0;
  const next = days.map((day) => {
    if (day === source || !day.is_open) return day;
    copied += 1;
    return {
      ...day, opens: source.opens, closes: source.closes,
      break_from: source.break_from, break_to: source.break_to,
    };
  });
  return { days: next, copied, source };
}

// Errori del server di una riga: { campo: messaggio } (il primo per campo).
export function errorsFor(errors, scope, index) {
  const found = {};
  (errors || []).forEach((error) => {
    if (error.scope === scope && error.index === index && !(error.field in found)) {
      found[error.field] = error.message;
    }
  });
  return found;
}

export function generalErrors(errors) {
  return (errors || []).filter((error) => error.scope === "general").map((error) => error.message);
}
